<script lang="ts" setup>
const { t } = useI18n();

const props = defineProps<{
  mode: string;
  type: string;
  address?: any;
  cancelView?: string | null;
  makeDefaultTitle?: string | null;
}>();

const emit = defineEmits(["formSubmit", "changeView"]);

const config = useRuntimeConfig();

const addressId = ref();
const name = ref();
const postcode = ref();
const postcodeTxt = ref();
const province = ref();
const provinceTxt = ref();
const city = ref();
const cityTxt = ref();
const street = ref();
const streetRangeId = ref();
const streetNo = ref();
const makeDefault = ref(props.address?.pivot.is_default);

const nameError = ref();
const postcodeError = ref();
const cityError = ref();
const streetError = ref();
const streetNoError = ref();

// -----------------------
// methods
// -----------------------
const validateForm = () => {
  let errorHappened = false;

  nameError.value = "";
  postcodeError.value = "";
  cityError.value = "";
  streetError.value = "";
  streetNoError.value = "";

  if (!name.value) {
    nameError.value = "checkout.errors.address_name";
    errorHappened = true;
  }
  if (!postcode.value) {
    postcodeError.value = "checkout.errors.shipping_postcode";
    errorHappened = true;
  }
  if (!city.value) {
    cityError.value = "checkout.errors.shipping_city";
    errorHappened = true;
  }
  if (!street.value) {
    streetError.value = "checkout.errors.shipping_street";
    errorHappened = true;
  }
  if (!streetNo.value) {
    streetNoError.value = "checkout.errors.shipping_street_no";
    errorHappened = true;
  }

  return !errorHappened;
};

const submitForm = () => {
  const valid = validateForm();
  if (valid) {
    const formData = {
      id: addressId.value,
      type: props.type,
      country_id: "ro",
      name: name.value,
      postcode: postcode.value,
      province: province.value,
      city: city.value,
      street_txt: street.value,
      street_range: streetRangeId.value,
      street_number: streetNo.value,
      make_default: makeDefault.value,
    };
    emit("formSubmit", formData);
  }
};

// -----------------------
// vue events
// -----------------------
onMounted(async () => {
  if (props.mode === "edit") {
    // postcode
    const postcodeFromMS = await useMeilisearch(
      config.public.meilisearchIndexPostcodes,
      {
        filter: ["id = " + props.address.postcode_id],
      },
    );
    if (Array.isArray(postcodeFromMS) && postcodeFromMS.length === 1) {
      postcodeTxt.value = postcodeFromMS[0].code;
    }

    // city
    const cityFromMS = await useMeilisearch(
      config.public.meilisearchIndexCities,
      {
        filter: ["id = " + props.address.city_id],
      },
    );
    if (Array.isArray(cityFromMS) && cityFromMS.length === 1) {
      cityTxt.value = cityFromMS[0].name;
    }

    // province
    const provinceFromMS = await useMeilisearch(
      config.public.meilisearchIndexProvinces,
      {
        filter: ["id = " + props.address.province_id],
      },
    );
    if (Array.isArray(provinceFromMS) && provinceFromMS.length === 1) {
      provinceTxt.value = provinceFromMS[0].name;
    }

    // console.log(props.address);
    addressId.value = props.address.id;
    name.value = props.address.name;
    postcode.value = props.address.postcode_id;
    province.value = props.address.province_id;
    city.value = props.address.city_id;
    street.value = props.address.street_txt;
    streetNo.value = props.address.street_number;
  }
});

const selectAddress = (item: any) => {
  // console.log("selected address: ", item);
  postcode.value = item.postcode_id;
  postcodeTxt.value = item.postcode_code;
  city.value = item.city_id;
  cityTxt.value = item.city_name;
  province.value = item.province_id;
  provinceTxt.value = item.province_name;
  street.value = item.street_name;
  streetRangeId.value = item.street_range_id;
  streetNo.value = null;
};
</script>
<template>
  <div>
    <div class="w-full py-3">
      <div class="mb-4 md:flex">
        <div class="w-full">
          <BaseInput
            v-model="name"
            :title="t('profile.new_address_name')"
            :mandatory="true"
            :error="nameError"
          />
        </div>
      </div>

      <div class="mb-4 py-1">
        <AddressSearchBox @select="selectAddress" />
      </div>
      <div class="mb-4 md:flex md:justify-between">
        <div class="w-full mb-4 md:mb-0 md:w-1/3 md:pr-2">
          <BaseInput
            v-model="postcodeTxt"
            :title="t('postcode')"
            :error="postcodeError"
            :disabled="true"
            :mandatory="true"
          />
        </div>
        <div class="w-full md:w-2/3 md:pl-2">
          <BaseInput
            v-model="provinceTxt"
            :title="t('province')"
            :mandatory="true"
            :disabled="true"
          />
        </div>
      </div>

      <div class="mb-4 md:flex md:justify-between">
        <div class="w-full mb-4 md:mb-0 md:w-1/2 md:pr-2">
          <BaseInput
            v-model="cityTxt"
            :title="t('city')"
            :error="cityError"
            :mandatory="true"
            :disabled="true"
          />
        </div>
        <div class="w-full md:w-1/2 md:pl-2">
          <BaseInput
            v-model="street"
            :error="streetError"
            :title="t('checkout.street')"
            :max-length="50"
            :mandatory="true"
          />
        </div>
      </div>

      <div class="mb-4 md:flex md:justify-between">
        <div class="w-full mb-4 md:mb-0">
          <BaseInput
            v-model="streetNo"
            :error="streetErrorNo"
            :title="t('checkout.street_no')"
            :max-length="50"
            :mandatory="true"
          />
        </div>
      </div>

      <div v-if="props.makeDefaultTitle" class="mb-4 md:flex">
        <BaseCheckbox2Way
          v-model="makeDefault"
          :label="t(props.makeDefaultTitle)"
        />
      </div>
    </div>

    <div class="flex flex-row">
      <BaseButton type="primary" class="mr-4" @click="submitForm"
        >{{ t("save") }}
      </BaseButton>
      <BaseButton
        v-if="props.cancelView"
        type="secondary"
        @mousedown.prevent="emit('changeView', 'cancelView')"
        >{{ t("cancel") }}
      </BaseButton>
    </div>
  </div>
</template>
